import CheckIcon from '@mui/icons-material/Check';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    Button,
    ButtonGroup,
    Checkbox,
    Divider,
    FormControl,
    Grid,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Joi from 'joi';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useAxios from '../../../../hooks/useAxios';
import useInventory from '../../../../hooks/useInventory';
import {
    arraySextforceAutoRepostActionPreviousPost,
    arraySextforceAutoRepostActionPreviousPostDisplay,
    schemaSextforceAutoRepost,
    SextforceAutoRepost,
    SextforceAutoRepostActionPreviousPost,
} from '../../../../hooks/useSextforceAutoRepost';
import useSextforceAutoRepostColors from '../../../../hooks/useSextforceAutoRepostColors';
import useSextforceCaptions from '../../../../hooks/useSextforceCaptions';
import useSubscriberPosts from '../../../../hooks/useSubscriberPosts';
import { SettingsContext } from '../../../../store/SettingsContext';
import { UserContext } from '../../../../store/UserContext';
import { getPostIdFromPostUrl, handleHttpError } from '../../../../utils/common';
import InventoryTemplateTagsSelector from '../../../common/InventoryTemplateTagsSelector';
import InventoryTemplateVariantsSelector from '../../../common/InventoryTemplateVariantsSelector';
import OnlyFansAlbumSelector from '../../../common/OnlyFansAlbumSelector';
import TemplateModeToggle, { TemplateSubMode } from '../../../common/TemplateModeToggle';
import OnlyFansPostPreview from '../../../common/OnlyFansPostPreview';
import StyledCard from '../../../common/StyledCard';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';

const momentPeriods: { [key: string]: string } = {
    minutes: 'Minutes',
    hours: 'Hours',
    days: 'Days',
    weeks: 'Weeks',
    months: 'Months',
};

type Props = {
    subscriber: any;
    refetchAutoReposts: () => void;
    handleCancel: () => void;
    setShowAddNewForm: (show: boolean) => void;
};

const SextforceAutoRepostNewRepost = (props: Props) => {
    const { subscriber, refetchAutoReposts, handleCancel, setShowAddNewForm } = props;
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const axios = useAxios();
    const dialog = useDialog();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [formData, setFormData] = useState<SextforceAutoRepost>({
        subscriberId: subscriber._id,
        name: '',
        description: '',
        colorId: undefined,
        active: true,
        originalPostUrl: '',
        type: 'link',
        repostEveryAmount: 1,
        repostEveryUnit: 'hours',
        repostWhenCampaignHitsTarget: undefined,
        pinPost: false,
        repostCount: 0,
        privateArchiveAfterActive: false,
        privateArchiveAfterAmount: 1,
        privateArchiveAfterUnit: 'hours',
        actionPreviousPost: 'none',
        nextRepostDate: new Date(),
        startDate: undefined,
        endDate: undefined,
        isPostTipCampaign: false,
        repostAfterTipCount: undefined,
        postId: undefined,
        sourceAlbumId: undefined,
        postCaption: false,
        captionCategory: '',
        postImage: true,
        postVideo: true,
        postAudio: false,
        postGif: false,
        templateIds: [],
        templateTags: [],
        templateTagsPaidOnly: true,
    });
    const [templateSubMode, setTemplateSubMode] = useState<TemplateSubMode>('ids');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [postId, setPostId] = useState<number>(0);
    const [isPostValid, setIsPostValid] = useState<boolean>(false);
    const [isPostTipCampaign, setIsPostTipCampaign] = useState<boolean>(false);
    const { data: post, isLoading: isPostLoading } = useSubscriberPosts(postId);
    const { data: sextforceAutoRepostColors, isLoading: isSextforceAutoRepostColorsLoading } = useSextforceAutoRepostColors();
    const { data: contentInventory, isLoading: isContentInventoryLoading } = useInventory();
    const { categories: captionCategories = [] } = useSextforceCaptions(true);

    // Validate the form data
    const errors: Joi.ValidationErrorItem[] | null = schemaSextforceAutoRepost.validate(formData).error?.details || null;
    const hasAtLeastOneAlbumMedia = !!(formData.postImage || formData.postVideo || formData.postAudio || formData.postGif);
    const selectedTemplateIds = formData.templateIds || [];
    const selectedTemplateTags = formData.templateTags || [];
    const isPaidPage = Number(subscriber?.ofSubscriptionPrice || 0) !== 0;

    // Check if the campaign URL is a valid URL and extract the post ID
    useEffect(() => {
        if (!formData.originalPostUrl) {
            return;
        }

        const parsedPostId = getPostIdFromPostUrl(formData.originalPostUrl);

        if (parsedPostId !== 0 && postId !== parsedPostId) {
            // Set the campaign ID
            setPostId(parsedPostId);
        }
    }, [formData.originalPostUrl]);

    useEffect(() => {
        if (post && !isPostLoading && post.canEdit) {
            setIsPostValid(true);

            // Is post a tip campaign?
            const isPostTipCampaign = post.fundRaising && post.fundRaising.presets && post.fundRaising.presets.length > 0 ? true : false;

            setIsPostTipCampaign(isPostTipCampaign);

            // set the post ID
            setFormData({
                ...formData,
                postId: post.id,
                isPostTipCampaign,
            });
        } else {
            setIsPostValid(false);
            setIsPostTipCampaign(false);

            // reset the post ID
            setFormData({ ...formData, postId: undefined, isPostTipCampaign: false });
        }
    }, [post, isPostLoading]);

    const autoRepostAdd = async (autoRepost: SextforceAutoRepost) => {
        if (!userContext.jwtToken || !settingsContext.apiKey) {
            return;
        }

        setIsSubmitting(true);

        const data = await axios
            .post(`${settingsContext.routes.autoRepost.base}${params.userId}`, autoRepost)
            .then(async response => {
                setIsSubmitting(false);
                return response.data as SextforceAutoRepost;
            })
            .catch(error => {
                setIsSubmitting(false);
                handleHttpError(error, dialog);

                return null;
            });

        return data;
    };

    const handleSubmit = () => {
        if (formData.type === 'album' && !hasAtLeastOneAlbumMedia) {
            enqueueSnackbar('Select at least one media type for album repost.', { variant: 'warning' });
            return;
        }

        if (formData.type === 'template' && templateSubMode === 'ids' && selectedTemplateIds.length === 0) {
            enqueueSnackbar('Select at least one template.', { variant: 'warning' });
            return;
        }

        if (formData.type === 'template' && templateSubMode === 'tags' && selectedTemplateTags.length === 0) {
            enqueueSnackbar('Select at least one template tag.', { variant: 'warning' });
            return;
        }

        autoRepostAdd(formData).then(data => {
            if (!data) {
                return;
            }

            refetchAutoReposts();

            enqueueSnackbar('Auto Repost added successfully.', {
                variant: 'success',
                autoHideDuration: 3000,
            });

            // Reset the form
            setFormData({
                subscriberId: subscriber._id,
                name: '',
                description: '',
                active: true,
                originalPostUrl: '',
                type: 'link',
                repostEveryAmount: 1,
                repostEveryUnit: 'hours',
                repostWhenCampaignHitsTarget: undefined,
                pinPost: false,
                repostCount: 0,
                privateArchiveAfterActive: false,
                privateArchiveAfterAmount: 1,
                privateArchiveAfterUnit: 'hours',
                actionPreviousPost: 'none',
                nextRepostDate: new Date(),
                startDate: undefined,
                endDate: undefined,
                sourceAlbumId: undefined,
                postCaption: false,
                postImage: true,
                postVideo: true,
                postAudio: false,
                postGif: false,
                templateIds: [],
                templateTags: [],
                templateTagsPaidOnly: true,
            });
            setTemplateSubMode('ids');

            setShowAddNewForm(false);
        });
    };

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <StyledCard noCard noBottomMargin>
                    <StyledCardTitleBar title="Add New Repost" />
                </StyledCard>
                <StyledCard>
                    <Grid container alignItems={'center'} justifyContent={'center'} spacing={1.5}>
                        <Grid item xs={12}>
                            Name
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                Name of this promo or the person you are promoting.{' '}
                                <em>This will be shown in the Telegram notification to your colleagues.</em>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id="name"
                                name="name"
                                size="small"
                                fullWidth
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                error={!!errors?.find(error => error.context?.key === 'name')}
                                helperText={errors ? errors.find(error => error.context?.key === 'name')?.message : undefined}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            Description
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                Any description or notes you would like to leave for this promo. These will not appear in the Telegram
                                notification to your colleagues.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id="description"
                                name="description"
                                size="small"
                                fullWidth
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                error={!!errors?.find(error => error.context?.key === 'description')}
                                helperText={errors ? errors.find(error => error.context?.key === 'description')?.message : undefined}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            Category Color
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                Choose an identifying color for this repost. This will be used to help identify the category or topic of the
                                repost.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Select
                                id="color"
                                size="small"
                                fullWidth
                                value={formData.colorId || 'none'}
                                onChange={e =>
                                    setFormData({ ...formData, colorId: e.target.value === 'none' ? undefined : e.target.value })
                                }
                            >
                                <MenuItem value="none">
                                    <Stack direction="row" spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                                        <CircleOutlinedIcon
                                            color="disabled"
                                            sx={{
                                                diaplay: 'inline-block',
                                                fontSize: 20,
                                            }}
                                        />
                                        <Typography variant="body1" color={'text.primary'}>
                                            None
                                        </Typography>
                                    </Stack>
                                </MenuItem>
                                {!isSextforceAutoRepostColorsLoading &&
                                    sextforceAutoRepostColors &&
                                    sextforceAutoRepostColors.map(color => (
                                        <MenuItem key={color._id} value={color._id}>
                                            <Stack direction="row" spacing={1} alignItems={'center'} justifyContent={'flex-start'}>
                                                <CircleIcon
                                                    sx={{
                                                        diaplay: 'inline-block',
                                                        color: color.color,
                                                        fontSize: 20,
                                                    }}
                                                />
                                                <Typography variant="body1" color={'text.primary'}>
                                                    {color.name}
                                                </Typography>
                                            </Stack>
                                        </MenuItem>
                                    ))}
                            </Select>
                        </Grid>

                        <Grid item xs={12}>
                            Repost Type
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                Share a link to the original post (great for large tip campaign and posts with promo links), post the whole
                                post again (great for PPVs, short-lived tip campaigns, etc.), post random content from an album (perfect for
                                keeping the feed less spammy and fresh), post random content from a template in your inventory (perfect for
                                PPVs and hands-free sales), or post a random caption from your Sextforce captions as a text-only post
                                (perfect for keeping your feed active between media posts).
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <ButtonGroup fullWidth orientation={isMobile ? 'vertical' : 'horizontal'}>
                                <Button
                                    variant={formData.type === 'link' ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="large"
                                    onClick={() => setFormData({ ...formData, type: 'link', privateArchiveAfterActive: false })}
                                    sx={{
                                        wordBreak: 'keep-all',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Share Link
                                </Button>
                                <Button
                                    variant={formData.type === 'post' ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="large"
                                    onClick={() => setFormData({ ...formData, type: 'post' })}
                                    sx={{
                                        wordBreak: 'keep-all',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Repeat Post
                                </Button>
                                <Button
                                    variant={formData.type === 'caption' ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="large"
                                    onClick={() =>
                                        setFormData({
                                            ...formData,
                                            type: 'caption',
                                            originalPostUrl: '',
                                            sourceAlbumId: undefined,
                                            templateIds: [],
                                            templateTags: [],
                                            postCaption: true,
                                            privateArchiveAfterActive: false,
                                        })
                                    }
                                    sx={{
                                        wordBreak: 'keep-all',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Caption
                                </Button>
                                <Button
                                    variant={formData.type === 'album' ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="large"
                                    onClick={() =>
                                        setFormData({
                                            ...formData,
                                            type: 'album',
                                            originalPostUrl: '',
                                            privateArchiveAfterActive: false,
                                        })
                                    }
                                    sx={{
                                        wordBreak: 'keep-all',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Album
                                </Button>
                                <Button
                                    variant={formData.type === 'template' ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="large"
                                    onClick={() =>
                                        setFormData({
                                            ...formData,
                                            type: 'template',
                                            originalPostUrl: '',
                                            sourceAlbumId: undefined,
                                            privateArchiveAfterActive: false,
                                        })
                                    }
                                    sx={{
                                        wordBreak: 'keep-all',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Template
                                </Button>
                            </ButtonGroup>
                        </Grid>
                        {formData.type === 'post' && (
                            <Grid item xs={12}>
                                <Alert severity="warning" icon={false}>
                                    If your original post has a Tracking Link (Campaign) or a Free Trial Link, please be mindful that there
                                    is a limit on OnlyFans of 3 new posts of campaign or free trial links per day! It is best to use a
                                    repost link for these. Also, OnlyFans require that the #ad hashtag is used on all these posts.
                                </Alert>
                            </Grid>
                        )}
                        {formData.type === 'album' && (
                            <>
                                <Grid item xs={12}>
                                    Source Album
                                    <Typography variant="body2" color={'text.secondary'} marginTop={0.5} marginBottom={0.5}>
                                        Select an album to post content from. The system will create a new post with content from this
                                        album.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <OnlyFansAlbumSelector
                                        size="small"
                                        vaultAlbum={formData.sourceAlbumId ?? 0}
                                        handleAlbumChange={e => {
                                            const v = e.target.value;
                                            setFormData({
                                                ...formData,
                                                sourceAlbumId: v === '' ? undefined : Number.parseInt(v, 10),
                                            });
                                        }}
                                        disabled={false}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    Content to Include
                                    <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                        Which types of content from the album should be posted?
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack direction="column" flexWrap="wrap" gap={0} useFlexGap>
                                        <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Checkbox
                                                    size="medium"
                                                    checked={!!formData.postImage}
                                                    onChange={e => setFormData({ ...formData, postImage: e.target.checked })}
                                                />
                                                <Typography variant="body2">Images</Typography>
                                            </Stack>
                                        </FormControl>
                                        <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Checkbox
                                                    size="medium"
                                                    checked={!!formData.postVideo}
                                                    onChange={e => setFormData({ ...formData, postVideo: e.target.checked })}
                                                />
                                                <Typography variant="body2">Videos</Typography>
                                            </Stack>
                                        </FormControl>
                                        <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Checkbox
                                                    size="medium"
                                                    checked={!!formData.postAudio}
                                                    onChange={e => setFormData({ ...formData, postAudio: e.target.checked })}
                                                />
                                                <Typography variant="body2">Audio</Typography>
                                            </Stack>
                                        </FormControl>
                                        <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Checkbox
                                                    size="medium"
                                                    checked={!!formData.postGif}
                                                    onChange={e => setFormData({ ...formData, postGif: e.target.checked })}
                                                />
                                                <Typography variant="body2">GIFs</Typography>
                                            </Stack>
                                        </FormControl>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl component="fieldset" sx={{ mt: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Checkbox
                                                size="medium"
                                                checked={!!formData.postCaption}
                                                onChange={e => setFormData({ ...formData, postCaption: e.target.checked })}
                                            />
                                            <Typography variant="body2">Include a random caption</Typography>
                                        </Stack>
                                    </FormControl>
                                </Grid>
                                {formData.postCaption && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color={'text.secondary'} marginBottom={0.5}>
                                            Caption category (optional)
                                        </Typography>
                                        <Select
                                            fullWidth
                                            size="small"
                                            value={formData.captionCategory || ''}
                                            onChange={e => setFormData({ ...formData, captionCategory: e.target.value })}
                                        >
                                            <MenuItem value="">All captions</MenuItem>
                                            {captionCategories.map(category => (
                                                <MenuItem key={category._id} value={category.name}>
                                                    {category.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                )}
                            </>
                        )}
                        {formData.type === 'template' && (
                            <>
                                <Grid item xs={12}>
                                    Templates
                                    <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                        Select one or more templates from content inventory, or select one or more template tags to draw
                                        from every template carrying any of those tags. One matching template will be selected at random
                                        for each repost. The system will create a new post with the same content, locked price, label
                                        settings and tagging from the selected template.
                                    </Typography>
                                </Grid>
                                {isPaidPage && (
                                    <Grid item xs={12}>
                                        <Alert severity="warning" icon={false}>
                                            Paid posts are not allowed on a paid page! Auto Reposts that post paid PPVs will get
                                            deactivated.
                                        </Alert>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <TemplateModeToggle
                                        value={templateSubMode}
                                        onChange={nextMode => {
                                            setTemplateSubMode(nextMode);
                                            setFormData({ ...formData, templateIds: [], templateTags: [] });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    {templateSubMode === 'ids' ? (
                                        <>
                                            <InventoryTemplateVariantsSelector
                                                inventory={contentInventory}
                                                loading={isContentInventoryLoading}
                                                selectedTemplates={selectedTemplateIds}
                                                onChange={nextTemplates => setFormData({ ...formData, templateIds: nextTemplates })}
                                            />
                                            {!!errors?.find(error => error.context?.key === 'templateIds') && (
                                                <Typography variant="caption" color="error">
                                                    {errors.find(error => error.context?.key === 'templateIds')?.message}
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <InventoryTemplateTagsSelector
                                            inventory={contentInventory}
                                            loading={isContentInventoryLoading}
                                            value={selectedTemplateTags}
                                            onChange={nextTags => setFormData({ ...formData, templateTags: nextTags })}
                                            paidOnly={formData.templateTagsPaidOnly ?? true}
                                            onPaidOnlyChange={paidOnly => setFormData({ ...formData, templateTagsPaidOnly: paidOnly })}
                                        />
                                    )}
                                </Grid>
                            </>
                        )}
                        {formData.type === 'caption' && (
                            <>
                                <Grid item xs={12}>
                                    Caption Category
                                    <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                        A random caption will be selected from your Sextforce captions and posted as-is as a text-only post.
                                        Choose a category to pick from, or leave it on All captions.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={formData.captionCategory || ''}
                                        onChange={e => setFormData({ ...formData, captionCategory: e.target.value })}
                                    >
                                        <MenuItem value="">All captions</MenuItem>
                                        {captionCategories.map(category => (
                                            <MenuItem key={category._id} value={category.name}>
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                            </>
                        )}
                        {formData.type !== 'album' && formData.type !== 'template' && formData.type !== 'caption' && (
                            <>
                                <Grid item xs={12}>
                                    Original Post's URL
                                    <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                        The URL to the post you want to repost. You can get this by clicking the share button on the post
                                        and copying the link.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack direction={'column'} spacing={0} sx={{ mb: 1 }}>
                                        <TextField
                                            id="originalPostUrl"
                                            name="originalPostUrl"
                                            placeholder="https://onlyfans.com/1234567890/username"
                                            size="small"
                                            value={formData.originalPostUrl}
                                            onChange={e => setFormData({ ...formData, originalPostUrl: e.target.value })}
                                            error={!!errors?.find(error => error.context?.key === 'originalPostUrl')}
                                            helperText={
                                                errors ? errors.find(error => error.context?.key === 'originalPostUrl')?.message : undefined
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <ContentPasteIcon
                                                        color="action"
                                                        onClick={() => {
                                                            navigator.clipboard.readText().then(clipText => {
                                                                setFormData({ ...formData, originalPostUrl: clipText });
                                                            });
                                                        }}
                                                        style={{ cursor: 'pointer' }}
                                                        sx={{
                                                            mr: 0.5,
                                                            ':hover': {
                                                                color: theme.palette.primary.dark,
                                                            },
                                                        }}
                                                    />
                                                ),
                                                endAdornment: post && post.canEdit && <CheckIcon color={'success'} />,
                                            }}
                                            sx={{ mb: 0 }}
                                        />
                                        {isPostLoading && <LinearProgress color="primary" />}
                                        {post && !post.canEdit && (
                                            <Typography variant="body2" color={'error'} marginTop={0.5}>
                                                This post is not valid or you do not have permission to repost it.
                                            </Typography>
                                        )}
                                    </Stack>
                                </Grid>
                                {post && !isPostLoading && isPostValid && formData.postId !== 0 && (
                                    <Grid item xs={12}>
                                        <OnlyFansPostPreview post={post} />
                                    </Grid>
                                )}
                            </>
                        )}

                        <Grid item xs={12}>
                            Start Date/Time
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                When should the first repost happen? Use this to stagger your reposts so they don't all happen at the same.{' '}
                                <em>Default: now.</em>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container alignItems={'center'} justifyContent={'center'} spacing={1}>
                                <Grid item xs="auto">
                                    <Checkbox
                                        size="medium"
                                        checked={!!formData.startDate}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                setFormData({ ...formData, startDate: new Date() });
                                            } else {
                                                setFormData({ ...formData, startDate: undefined });
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs>
                                    <MobileDateTimePicker
                                        disabled={!formData.startDate}
                                        value={formData.startDate}
                                        ampm={true}
                                        onChange={date => setFormData({ ...formData, startDate: moment(date).toDate() || undefined })}
                                        renderInput={props => <TextField fullWidth size="small" {...props} />}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            Repost Every
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                How often should the post be reposted? There is a daily limit of 400 posts on OnlyFans.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Alert severity="warning" icon={false}>
                                Note: There is no point in reposting posts too frequently because nobody will get a chance to see them -
                                especially if you have many auto reposts setup at the same time. They will just be fighting each other for
                                attention. Make sure to look at the statistics of each repost here to see how many users viewed the post.
                            </Alert>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container alignItems={'center'} justifyContent={'center'} spacing={1}>
                                <Grid item xs>
                                    <TextField
                                        id="repostEveryAmount"
                                        name="repostEveryAmount"
                                        fullWidth
                                        size="small"
                                        value={Number.isNaN(formData.repostEveryAmount) ? '' : formData.repostEveryAmount}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                repostEveryAmount: Number.isNaN(Number.parseInt(e.target.value, 10))
                                                    ? 0
                                                    : Number.parseInt(e.target.value, 10),
                                            })
                                        }
                                        error={!!errors?.find(error => error.context?.key === 'repostEveryAmount')}
                                        helperText={
                                            errors ? errors.find(error => error.context?.key === 'repostEveryAmount')?.message : undefined
                                        }
                                    />
                                </Grid>
                                <Grid item xs="auto">
                                    <Select
                                        value={formData.repostEveryUnit}
                                        onChange={e => {
                                            setFormData({ ...formData, repostEveryUnit: e.target.value as moment.DurationInputArg2 });
                                        }}
                                        fullWidth
                                        size="small"
                                        sx={{ mt: '4px' }}
                                    >
                                        {Object.keys(momentPeriods).map(item => (
                                            <MenuItem value={item} key={item}>
                                                {momentPeriods[item]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            Repost When Tip Campaign Hits Target
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                Should the post be reposted when the tip campaign hits its target and not wait for the repost time?
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <ButtonGroup fullWidth disabled={!isPostTipCampaign || formData.type !== 'post'}>
                                <Button
                                    variant={formData.repostWhenCampaignHitsTarget ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="large"
                                    onClick={() => setFormData({ ...formData, repostWhenCampaignHitsTarget: true })}
                                >
                                    Yes
                                </Button>
                                <Button
                                    variant={!formData.repostWhenCampaignHitsTarget ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="large"
                                    onClick={() => setFormData({ ...formData, repostWhenCampaignHitsTarget: false })}
                                >
                                    No
                                </Button>
                            </ButtonGroup>
                        </Grid>
                        <Grid item xs={12}>
                            Until Date (optional)
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                If you want the repost to stop at a specific date, enter it here. If you don't enter a date, the repost will
                                continue indefinitely.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container alignItems={'center'} justifyContent={'center'} spacing={1}>
                                <Grid item xs="auto">
                                    <Checkbox
                                        size="medium"
                                        checked={!!formData.endDate}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                setFormData({ ...formData, endDate: new Date() });
                                            } else {
                                                setFormData({ ...formData, endDate: undefined });
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs>
                                    <MobileDateTimePicker
                                        disabled={!formData.endDate}
                                        value={formData.endDate}
                                        onChange={date => setFormData({ ...formData, endDate: date ? moment(date).toDate() : undefined })}
                                        renderInput={props => <TextField fullWidth size="small" {...props} />}
                                        ampm={true}
                                        ampmInClock={true}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        {formData.type === 'link' && (
                            <>
                                <Grid item xs={12}>
                                    Random Caption
                                    <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                        A random caption will be selected from your Sextforce captions and the link will be added before the
                                        shared link. If you don't have any captions, the link will be posted without a caption.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl component="fieldset" sx={{ mt: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Checkbox
                                                size="medium"
                                                checked={!!formData.postCaption}
                                                onChange={e => setFormData({ ...formData, postCaption: e.target.checked })}
                                            />
                                            <Typography variant="body2">Include a random caption</Typography>
                                        </Stack>
                                    </FormControl>
                                    <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                        A random caption will be selected from your Sextforce captions and the link will be added on a new
                                        line below it.
                                    </Typography>
                                </Grid>
                                {formData.postCaption && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color={'text.secondary'} marginBottom={0.5}>
                                            Caption category (optional)
                                        </Typography>
                                        <Select
                                            fullWidth
                                            size="small"
                                            value={formData.captionCategory || ''}
                                            onChange={e => setFormData({ ...formData, captionCategory: e.target.value })}
                                        >
                                            <MenuItem value="">All captions</MenuItem>
                                            {captionCategories.map(category => (
                                                <MenuItem key={category._id} value={category.name}>
                                                    {category.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                )}
                            </>
                        )}

                        <Grid item xs={12}>
                            Pin Post
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                Should the new post be pinned to the top of your feed?
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <ButtonGroup fullWidth>
                                <Button
                                    variant={formData.pinPost ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="large"
                                    onClick={() => setFormData({ ...formData, pinPost: true })}
                                >
                                    Yes
                                </Button>
                                <Button
                                    variant={!formData.pinPost ? 'contained' : 'outlined'}
                                    color="primary"
                                    size="large"
                                    onClick={() => setFormData({ ...formData, pinPost: false })}
                                >
                                    No
                                </Button>
                            </ButtonGroup>
                        </Grid>
                        <Grid item xs={12}>
                            Hide/Expire Post
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                The system will private archive the post after a the time period you set here to hide the post from your
                                feed, but allow the system to repost it again next time. It's similar to the "Expire Post" feature on
                                OnlyFans, but you can set your own time period.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container alignItems={'center'} justifyContent={'center'} spacing={1}>
                                <Grid item xs="auto">
                                    <Checkbox
                                        size="medium"
                                        checked={!!formData.privateArchiveAfterActive}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                privateArchiveAfterActive: e.target.checked,
                                                ...(!e.target.checked
                                                    ? {
                                                          privateArchiveAfterAmount: 1,
                                                          privateArchiveAfterUnit: 'hours',
                                                      }
                                                    : {}),
                                                ...(e.target.checked &&
                                                formData.type === 'post' &&
                                                formData.actionPreviousPost === 'privateArchive'
                                                    ? { actionPreviousPost: 'none' }
                                                    : {}),
                                            })
                                        }
                                        sx={{ mt: '4px' }}
                                    />
                                </Grid>
                                <Grid item xs={true}>
                                    <TextField
                                        disabled={!formData.privateArchiveAfterActive}
                                        id="privateArchiveAfterAmount"
                                        name="privateArchiveAfterAmount"
                                        fullWidth
                                        size="small"
                                        value={Number.isNaN(formData.privateArchiveAfterAmount) ? '' : formData.privateArchiveAfterAmount}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                privateArchiveAfterAmount: Number.isNaN(Number.parseInt(e.target.value, 10))
                                                    ? 0
                                                    : Number.parseInt(e.target.value, 10),
                                            })
                                        }
                                        error={!!errors?.find(error => error.context?.key === 'privateArchiveAfterAmount')}
                                        helperText={
                                            errors
                                                ? errors.find(error => error.context?.key === 'privateArchiveAfterAmount')?.message
                                                : undefined
                                        }
                                    />
                                </Grid>
                                <Grid item xs="auto">
                                    <Select
                                        disabled={!formData.privateArchiveAfterActive}
                                        value={formData.privateArchiveAfterUnit}
                                        onChange={e => {
                                            setFormData({
                                                ...formData,
                                                privateArchiveAfterUnit: e.target.value as moment.DurationInputArg2,
                                            });
                                        }}
                                        fullWidth
                                        size="small"
                                        sx={{ mt: '4px' }}
                                    >
                                        {Object.keys(momentPeriods).map(item => (
                                            <MenuItem value={item} key={item}>
                                                {momentPeriods[item]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            What To Do With The Previous Repost?
                            <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                What should be done with the previous post when the new one is posted? If you choose to delete or archive
                                the post then the post will be deleted or archived after the new post is posted.{' '}
                                <em>
                                    Note: if you've chosen to repost a link, the original post will not be touched. Only the following
                                    reposts will be affected by this setting.
                                </em>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Select
                                id="actionPreviousPost"
                                fullWidth
                                size="small"
                                value={formData.actionPreviousPost}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        actionPreviousPost: e.target.value as SextforceAutoRepostActionPreviousPost,
                                    })
                                }
                            >
                                {arraySextforceAutoRepostActionPreviousPost.map(action => (
                                    <MenuItem
                                        key={action}
                                        value={action}
                                        disabled={
                                            formData.privateArchiveAfterActive && action === 'privateArchive' && formData.type === 'post'
                                                ? true
                                                : false
                                        }
                                        sx={{
                                            color: action === 'delete' && formData.type === 'post' ? theme.palette.error.main : 'inherit',
                                        }}
                                    >
                                        {`${
                                            arraySextforceAutoRepostActionPreviousPostDisplay[
                                                arraySextforceAutoRepostActionPreviousPost.indexOf(action)
                                            ]
                                        }${action === 'delete' && formData.type === 'post' ? ' (Are you sure?)' : ''}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        {formData.actionPreviousPost === 'privateArchive' &&
                            formData.type === 'post' &&
                            formData.privateArchiveAfterActive && (
                                <Grid item xs={12}>
                                    <Alert severity="warning" icon={false}>
                                        You have chosen to private archive the post after a certain time. You cannot then private archive
                                        the same post again.
                                    </Alert>
                                </Grid>
                            )}
                        {/* {formData.actionPreviousPost === 'delete' && (
                            <Grid item xs={12}>
                                <Alert severity="warning" icon={false}>
                                    OnlyFans has a limit of how many posts can be deleted every day. The limit is an inconvenient 20% of the
                                    total number of posts. Keep that in mind when you set the frequency of your reposts.
                                </Alert>
                            </Grid>
                        )} */}
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                        <Grid item xs={8}>
                            <LoadingButton
                                variant="contained"
                                color="secondary"
                                fullWidth
                                size="large"
                                loading={isSubmitting}
                                disabled={
                                    !!errors ||
                                    (formData.type !== 'album' &&
                                        formData.type !== 'template' &&
                                        formData.type !== 'caption' &&
                                        !isPostValid) ||
                                    (formData.type === 'album' &&
                                        (formData.sourceAlbumId == null || formData.sourceAlbumId < 1 || !hasAtLeastOneAlbumMedia)) ||
                                    (formData.type === 'template' &&
                                        (templateSubMode === 'ids' ? selectedTemplateIds.length === 0 : selectedTemplateTags.length === 0))
                                }
                                onClick={handleSubmit}
                            >
                                <span>Add New Repost</span>
                            </LoadingButton>
                        </Grid>
                        <Grid item xs={4}>
                            <Button variant="outlined" color="error" fullWidth size="large" onClick={handleCancel}>
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                </StyledCard>
            </LocalizationProvider>
        </>
    );
};

export default SextforceAutoRepostNewRepost;
